import {HttpErrorResponse, HttpEventType, HttpUploadProgressEvent} from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MAT_INPUT_VALUE_ACCESSOR} from '@angular/material/input';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FileResponse} from '@middleware';
import {FileUploadDialogData, FileUploadManager, FileUploadManagerMap} from '@models/file-upload';
import {ALLOWED_MIME_TYPES} from '@shared/constants/constants';

@Component({
  standalone: true,
  selector: 'app-file-upload-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressBarModule, MatCardModule],
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: FileUploadDialogComponent}],
})
export class FileUploadDialogComponent implements OnInit {
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef: MatDialogRef<FileUploadDialogComponent> = inject(MatDialogRef);
  public readonly data: FileUploadDialogData = inject(MAT_DIALOG_DATA);
  private readonly ngZone = inject(NgZone);

  @ViewChild('inputElement', {static: true})
  private readonly inputElementRef!: ElementRef<HTMLInputElement>;

  public readonly allowedMIMETypes = ALLOWED_MIME_TYPES.join(',');
  readonly existingFiles = signal<FileResponse[]>(this.data.fileResponses || []);
  readonly fileUploadManagerMap: WritableSignal<FileUploadManagerMap> = signal(new Map<string, FileUploadManager>());
  readonly pendingUploadManagerMap: WritableSignal<FileUploadManagerMap> = signal(new Map<string, FileUploadManager>());

  /** Computed list of new files awaiting upload, used by the HTML @for loop. */
  public readonly filesPendingUpload = computed(() => Array.from(this.pendingUploadManagerMap().values()));

  ngOnInit(): void {
    const initialManagers = new Map<string, FileUploadManager>();
    this.data.fileResponses?.forEach(file => {
      initialManagers.set(file.id, {
        id: file.id,
        isExisting: true,
        uploadComplete: true,
        inProgress: false,
        canRetry: false,
        progress: 100,
        fileResponses: [file],
        payload: undefined,
      } as FileUploadManager);
    });
    this.fileUploadManagerMap.set(initialManagers);
  }

  openFileBrowser(): void {
    this.ngZone.run(() => {
      this.inputElementRef.nativeElement.click();
    });
  }

  /**
   * Handles file selection.
   * New files are added to the list but not uploaded immediately.
   * @param event The change event from the file input.
   */
  async onChange(event: Event): Promise<void> {
    console.log('[FileUploadDialog] onChange event fired. Starting file processing...');

    await this.ngZone.run(async () => {
      const input = event.target as HTMLInputElement;
      const files = input.files;

      if (!files || files.length === 0) {
        return;
      }

      const newPendingManagers: FileUploadManagerMap = new Map(this.pendingUploadManagerMap());

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const tempId = `temp-${Date.now()}-${i}-${file.name}`;

        const newFum: FileUploadManager = {
          id: tempId,
          payload: {file: file},
          isExisting: false,
          inProgress: false,
          uploadComplete: false,
          progress: 0,
          errorMessage: '',
          canRetry: false,
          fileResponses: [],
        };

        newPendingManagers.set(tempId, newFum);
      }

      this.pendingUploadManagerMap.set(newPendingManagers);

      this.cdr.detectChanges();
    });
  }

  /** True if there is at least one new file selected but not yet uploaded. */
  public canUpload = computed(() => {
    return this.filesPendingUpload().length > 0;
  });

  /** Executes all pending uploads when the 'Upload' button is clicked. */
  onUpload(): void {
    if (!this.canUpload()) {
      return;
    }

    const currentMap = this.fileUploadManagerMap();
    const pendingMap = this.pendingUploadManagerMap();
    const mergedMap: FileUploadManagerMap = new Map([...currentMap, ...pendingMap]);

    this.fileUploadManagerMap.set(mergedMap);
    this.pendingUploadManagerMap.set(new Map());

    Array.from(pendingMap.values()).forEach(fum => {
      this.uploadFile(fum);
    });

    this.cdr.detectChanges();
  }

  /**
   * Initiates the upload process
   * @param fileUploadManager
   */
  uploadFile(fileUploadManager: FileUploadManager): void {
    // Cancel any existing subscription for retry logic
    fileUploadManager.uploadSub?.unsubscribe();

    this.pendingUploadManagerMap.update(map => {
      map.delete(fileUploadManager.id);
      return map;
    });

    this.setFileUploadManagerProps(fileUploadManager, {
      inProgress: true,
      canRetry: false,
      errorMessage: undefined,
      uploadComplete: false,
      progress: 0,
    });

    const uploadObservable = this.data.fileUploadFn(fileUploadManager);

    const subscription = uploadObservable.subscribe({
      next: (event: FileResponse | HttpUploadProgressEvent) => {
        if ((event as HttpUploadProgressEvent).type === HttpEventType.UploadProgress) {
          this.handleUploadProgress(fileUploadManager, event as HttpUploadProgressEvent);
        } else {
          // If it's not a progress event, it must be the final FileResponse
          this.handleUploadSuccess(fileUploadManager, event as FileResponse);
        }
      },
      error: err => {
        this.handleUploadError(fileUploadManager, err);
      },
      complete: () => {
        this.setFileUploadManagerProps(fileUploadManager, {
          inProgress: false,
          uploadSub: undefined,
        });
        this.cdr.detectChanges();
      },
    });

    this.setFileUploadManagerProps(fileUploadManager, {
      uploadSub: subscription,
    });
  }

  /**
   * Updates properties of a FileUploadManager signal safely and triggers change detection.
   * @param manager The manager instance to update.
   * @param props An object containing the properties to change.
   */
  setFileUploadManagerProps(manager: FileUploadManager, props: Partial<FileUploadManager>): void {
    this.fileUploadManagerMap.update(oldMap => {
      const currentManager = oldMap.get(manager.id);
      const updatedManager = {...currentManager, ...props} as FileUploadManager;
      const newMap = new Map(oldMap);

      newMap.set(manager.id, updatedManager);

      return newMap;
    });

    this.cdr.detectChanges();
  }

  handleUploadProgress(fileUploadManager: FileUploadManager, event: HttpUploadProgressEvent): void {
    fileUploadManager.progress = Math.round((event.loaded / (event.total || 1)) * 100);
  }

  /** Handles successful upload response. */
  handleUploadSuccess(fileUploadManager: FileUploadManager, fileResponse: FileResponse): void {
    fileUploadManager.uploadSub?.unsubscribe();

    this.setFileUploadManagerProps(fileUploadManager, {
      uploadComplete: true,
      inProgress: false,
      progress: 100,
      uploadSub: undefined,
      uploadPayload: undefined,
      payload: undefined,
      fileResponses: [fileResponse],
      isExisting: false,
    } as any);
    this.snackBar.open(`File uploaded successfully: ${fileResponse.file_name}`, 'Close', {duration: 3000});
  }

  /** Handles upload error response. */
  handleUploadError(fileUploadManager: FileUploadManager, err: HttpErrorResponse): void {
    fileUploadManager.uploadSub?.unsubscribe();

    const errorMessage = err.error?.message || `Upload failed: ${err.statusText}`;

    this.setFileUploadManagerProps(fileUploadManager, {
      inProgress: false,
      uploadComplete: false,
      errorMessage: errorMessage,
      canRetry: true,
      uploadSub: undefined,
    });
    this.displayError(errorMessage);
  }

  displayError(message: string): void {
    this.snackBar.open(message, 'Close', {duration: 5000, panelClass: ['snackbar-error']});
  }

  /**
   * Finalizes the dialog by collecting all successful uploads and non-deleted existing files.
   * This is called by the 'Close' button to return the final list to the parent component.
   */
  onClose(): void {
    const finalExistingFiles = this.existingFiles();

    const newUploadResponses = Array.from(this.fileUploadManagerMap().values())
      .filter(manager => manager.uploadComplete && manager.fileResponses?.length > 0 && !manager.isExisting)
      .flatMap(manager => manager.fileResponses);

    const updatedList = [...finalExistingFiles, ...newUploadResponses];

    this.dialogRef.close(updatedList);
  }
}
